/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import semverCompare from 'semver-compare';
import { get } from 'lodash';

import ApiCommon from './api-common';

const SUPPORTED_VERSION = '3.0';

export default class OpenApiV3 extends ApiCommon {
  /**
   * checks that the given file is a supported OpenAPI version
   */
  isSupported(openApi) {
    if (!openApi || typeof openApi !== 'object') {
      return false;
    }
    if (openApi.openapi) {
      return semverCompare(openApi.openapi, SUPPORTED_VERSION) >= 0;
    }

    return false;
  }

  cleanEndpoints(endpoints, options = {}) {
    let cleanEndpoints = [];
    endpoints.forEach((endpoint) => {
      let url = endpoint.url ? endpoint.url : endpoint.endpointUrl;
      if (options.basePath) {
        url = url + options.basePath;
      }
      cleanEndpoints = cleanEndpoints.concat({
        url: url,
        variables: endpoint.variables,
        types: this.getEndpointTypes(endpoint.type)
      });
    });
    return cleanEndpoints;
  }

  /**
   * get endpoints
   */
  getEndpoints(openApi, options = {}) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }
    let path = options.path;
    let method = options.method;
    let endpoints = [];
    if (get(openApi, `paths[${path}][${method}].servers`)) {
      endpoints = this.cleanEndpoints(openApi.paths[path][method].servers);
    } else if (get(openApi, `paths[${path}].servers`)) {
      endpoints = this.cleanEndpoints(openApi.paths[path].servers);
    } else if (openApi.endpoint) {
      endpoints = endpoints.concat({ url: openApi.endpoint });
    } else if (get(openApi, '[x-ibm-configuration][servers]', []).length > 0) {
      endpoints = this.cleanEndpoints(openApi['x-ibm-configuration']['servers']);
    } else if (get(openApi, '[x-ibm-endpoints]', []).length > 0) {
      endpoints = this.cleanEndpoints(openApi['x-ibm-endpoints'], { basePath: openApi.basePath });
    } else if (openApi.servers && openApi.servers.length > 0) {
      endpoints = this.cleanEndpoints(openApi['servers']);
    }
    // validate the endpoints are actually URLs not just URIs -- if they are
    // not URIs then add current hostname like we do for OAI2.
    for (var key in endpoints) {
      if (Object.prototype.hasOwnProperty.call(endpoints, key)) {
        endpoints[key].url = this.resolveRelativeUrl(endpoints[key].url);
      }
    }
    return endpoints;
  }

  /**
   * returns the body parameter, if applicable
   */
  getBodyParameter(operation, path, contentType, openApi) {
    if (!operation || !openApi || !this.isSupported(openApi)) {
      return;
    }

    if (openApi.openapi) {
      let bodyParam;

      if (operation.requestBody) {
        if (operation.requestBody.$ref) {
          operation.requestBody = this.getLocalReference(operation.requestBody.$ref, openApi);
        }

        if (operation.requestBody.content) {
          if (operation.requestBody.content[contentType]) {
            bodyParam = JSON.parse(JSON.stringify(operation.requestBody.content[contentType]));
          } else {
            let defaultType = Object.keys(operation.requestBody.content)[0];
            if (!operation.requestBody.content[defaultType]) {
              return operation.requestBody;
            }
            bodyParam = JSON.parse(JSON.stringify(operation.requestBody.content[defaultType]));
          }

          bodyParam.description = operation.requestBody.description;
          bodyParam.required = operation.requestBody.required;
          bodyParam.name = 'body';
          bodyParam.in = 'body';
          return bodyParam;
        } else {
          return operation.requestBody;
        }
      }
    }
  }

  /**
   * returns the form data parameters, if applicable
   */
  getFormDataParameters(operation, path, contentType, openApi) {
    if (!operation || !openApi || !this.isSupported(openApi)) {
      return;
    }

    if (openApi.openapi) {
      let formDataParams = [];

      if (
        operation.requestBody.content[contentType] &&
        operation.requestBody.content[contentType].schema &&
        operation.requestBody.content[contentType].schema.$ref
      ) {
        operation.requestBody.content[contentType].schema = this.getLocalReference(operation.requestBody.content[contentType].schema.$ref, openApi);
      }
      if (
        operation.requestBody.content[contentType] &&
        operation.requestBody.content[contentType].schema &&
        operation.requestBody.content[contentType].schema.properties
      ) {
        for (let parameter in operation.requestBody.content[contentType].schema.properties) {
          let newparam = JSON.parse(JSON.stringify(operation.requestBody.content[contentType].schema.properties[parameter]));
          newparam.name = parameter;
          newparam.in = 'formData';
          newparam.required = operation.requestBody.content[contentType].schema.required?.includes(newparam.name);
          formDataParams.push(newparam);
        }
      }
      return formDataParams;
    }
  }

  /**
   * Sets the config for the oauth security type definition
   */
  getSecurityFlowConfig(flow, definitions, credentials, endpoint) {
    if (!flow) {
      return;
    }

    let config = this.getSecurityFlowConfigDefaults();

    Object.keys(flow).forEach((securityDef) => {
      if (!this.securityDefinitionFound(definitions, securityDef)) {
        return;
      }

      let thisDef = definitions[securityDef];
      let thisFlow = flow[securityDef];

      config = this.setApikeySecurityTypeConfig(config, thisDef);
      config = this.setHttpSecurityTypeConfig(config, thisDef);
      config = this.setOauthSecurityTypeConfig(config, thisDef, thisFlow, credentials, endpoint);
      config = this.setOpenIdConnectTypeConfig(config, thisDef);
    });

    config = this.setRemainingConfig(config, credentials);

    return config;
  }

  setOpenIdConnectTypeConfig(config, securityDef) {
    if (securityDef.type === 'openIdConnect') {
      config.openIdConnectUrl = securityDef.openIdConnectUrl;
      config.requiresAuthorizationSection = true;
    }

    return config;
  }

  /**
   * Sets the config for the http security type definition
   */
  setHttpSecurityTypeConfig(config, securityDef, localscopes, credentials, endpoint) {
    if (securityDef.type === 'http') {
      config.requiresHttpAuth = true;
      config.httpScheme = securityDef.scheme;

      if (securityDef.scheme === 'basic') {
        config.requiresBasicAuth = true;
      }

      if (securityDef.scheme === 'bearer') {
        config.requiresHttpBearerToken = true;
        config.requiresAuthorizationSection = true;
      }

      if (securityDef.scheme === 'digest') {
        config.requiresDigestAuth = true;
      }

      if (securityDef.scheme === 'hoba') {
        config.requiresHobaAuth = true;
      }

      if (securityDef.scheme === 'scramSha256') {
        config.requiresScramShaAuth = true;
      }

      if (securityDef.scheme === 'vapid') {
        config.requiresVapidAuth = true;
      }

      if (securityDef.scheme === 'negotiate') {
        config.requiresNegotiateAuth = true;
      }

      if (securityDef.scheme === 'oauth') {
        let oauthFlowsConfig = {};

        for (let flow in securityDef.flows) {
          oauthFlowsConfig[flow] = this.setOauthFlowConfig(config, flow, securityDef, localscopes, credentials, endpoint);
        }

        config = oauthFlowsConfig[Object.keys(oauthFlowsConfig)[0]];
        config.oauthFlowsConfig = oauthFlowsConfig;
        config.requiresOauth = true;
      }

      if (securityDef.scheme === 'mutual') {
        config.requiresMutualAuth = true;
      }
    }

    return config;
  }

  /**
   * returns all schema definitions
   */
  getSchemas(openApi) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }
    if (openApi.components && openApi.components.schemas) {
      return openApi.components.schemas;
    }
  }

  /**
   * returns all schema definitions with their correct place in the object
   */
  getSchemasFullPath(openApi) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }

    if (openApi.components) {
      return { components: openApi.components };
    }
  }

  /**
   * Get the root of the definitions or schemas from the Object
   * returned by getSchemasFullPath();
   */
  getSchemasRoot(schemasFullPath) {
    let rootDefinitions = null;

    if (schemasFullPath.components && schemasFullPath.components.schemas) {
      rootDefinitions = schemasFullPath.components.schemas;
    }

    return rootDefinitions;
  }

  /**
   * returns all example definitions
   */
  getExamples(openApi) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }
    if (openApi.components && openApi.components.examples) {
      return openApi.components.examples;
    }
  }

  /**
   * returns all example definitions with their correct place in the object
   */
  getExamplesFullPath(openApi) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }

    if (openApi.components && openApi.components.examples) {
      return { components: { examples: openApi.components.examples } };
    }
  }

  /**
   * Get the root of the examples from the Object
   * returned by getExamplesFullPath();
   */
  getExamplesRoot(examplesFullPath) {
    let rootDefinitions = null;

    if (examplesFullPath.components && examplesFullPath.components.examples) {
      rootDefinitions = examplesFullPath.components.examples;
    }

    return rootDefinitions;
  }

  /**
   * returns all security definitions
   */
  getSecurityDefinitions(endpoint) {
    let securityDefinitions = get(this.api, 'components.securitySchemes');
    if (!securityDefinitions) return;

    let oauthServers = get(this.api, 'x-ibm-configuration.oauth-servers');
    if (oauthServers) {
      securityDefinitions = JSON.parse(JSON.stringify(securityDefinitions));
      Object.entries(oauthServers).forEach(([definitionName, flows]) => {
        Object.entries(flows).forEach(([flow, flowDefinition]) => {
          if (get(securityDefinitions, `${definitionName}.flows.${flow}`)) {
            securityDefinitions[definitionName].flows[flow].tokenUrl = flowDefinition.tokenUrls;
            securityDefinitions[definitionName].flows[flow].authorizationUrl = flowDefinition.authorizationUrls;
            securityDefinitions[definitionName].flows[flow].refreshUrl = flowDefinition.refreshUrls;
          }
        });
      });
    }
    // if there's an endpoint, we need to resolve the security definition urls
    if (endpoint) {
      securityDefinitions = this.resolveOauthUrls(securityDefinitions, endpoint);
    }

    return securityDefinitions;
  }

  /**
   * Sets the config for the oauth security type definition
   */
  setOauthSecurityTypeConfig(config, securityDef, localscopes, credentials, endpoint) {
    if (securityDef.type === 'oauth2') {
      let oauthFlowsConfig = {};

      for (let flow in securityDef.flows) {
        oauthFlowsConfig[flow] = this.setOauthFlowConfig(config, flow, securityDef, localscopes, credentials, endpoint);
      }
      if (oauthFlowsConfig[Object.keys(oauthFlowsConfig)[0]]) {
        config = oauthFlowsConfig[Object.keys(oauthFlowsConfig)[0]];
        config.oauthFlowsConfig = oauthFlowsConfig;
      }
    }

    return config;
  }

  /**
   * Sets the config for the spcific flow object in the oauth security
   * type definition
   */
  setOauthFlowConfig(originalConfig, flow, thisDef, localscopes, credentials, endpoint) {
    // Take a copy of the config object passed in so that we can update values in it
    let config = Object.assign({}, originalConfig);

    config.requiresOauth = true;
    config.requiresClientId = true;
    config.oauthFlow = flow;

    if (flow === 'clientCredentials' || flow === 'authorizationCode' || flow === 'password') {
      config.requiresClientSecret = true;
    }

    if (flow === 'implicit' || flow === 'authorizationCode') {
      config.requiresRedirectUri = true;
    }

    let urls = this.getFullOauthUrls(thisDef.flows[flow], endpoint);
    if (thisDef.flows[flow].authorizationUrl) {
      config.oauthAuthUrl = urls.authorizationUrl;
      config.requiresAuthorizationSection = true;
    }

    if (thisDef.flows[flow].tokenUrl) {
      config.oauthTokenUrl = urls.tokenUrl;
      config.requiresAuthorizationSection = true;
    }
    if (thisDef.flows[flow].refreshUrl) {
      config.oauthRefreshUrl = urls.refreshUrl;
      config.requiresAuthorizationSection = true;
    }

    if (thisDef.flows[flow].scopes && Object.keys(thisDef.flows[flow].scopes).length > 0) {
      config.oauthScopes = [];
      // remove any x- extensions
      for (let scope in thisDef.flows[flow].scopes) {
        if (scope.indexOf('x-') !== 0 && localscopes && localscopes.indexOf(scope) > -1) {
          config.oauthScopes.push(scope);
        }
      }
    }

    config = this.setRemainingConfig(config, credentials);

    return config;
  }

  /**
   * returns the supported content types for the operation
   */
  getContentTypes(operation, openApi) {
    if (!operation || !openApi || !this.isSupported(openApi)) {
      return [];
    }

    let contentTypes = [];
    if (operation.requestBody && operation.requestBody.content) {
      contentTypes = contentTypes.concat(Object.keys(operation.requestBody.content));
    }
    contentTypes = JSON.parse(JSON.stringify(contentTypes));

    // if the API is enforced and has a body parameter assume default content types
    if (contentTypes.length === 0 && openApi['x-ibm-configuration']) {
      // If there are no parameters, give up an return whatever we have so far
      if (!operation.parameters) {
        return contentTypes;
      }

      operation.parameters.forEach(function (parameter) {
        if (parameter.in === 'formData') {
          contentTypes.push('application/x-www-form-urlencoded');
        }
        if (parameter.in === 'body') {
          if (openApi['x-ibm-configuration']['enforced']) {
            contentTypes.push('application/json');
            contentTypes.push('application/xml');
          } else {
            contentTypes.push('application/json');
          }
        }
      });
    }
    return contentTypes;
  }

  /**
   * returns the supported accept types for the operation
   */
  getAccepts(operation, openApi) {
    if (!operation || !openApi || !this.isSupported(openApi)) {
      return [];
    }

    let accepts = [];

    if (operation.responses) {
      Object.keys(operation.responses).forEach((responseCode) => {
        let response = operation.responses[responseCode];
        if (response.$ref) {
          response = this.getLocalReference(response.$ref, openApi);
        }
        if ((responseCode === 'default' || responseCode.startsWith('2')) && response.content) {
          accepts = accepts.concat(Object.keys(response.content));
        }
      });
      accepts = accepts.filter((val, idx, arr) => arr.indexOf(val) === idx);
    }

    accepts = JSON.parse(JSON.stringify(accepts));

    if (accepts.length === 0) {
      // if the API is enforced assume default content types
      if (openApi['x-ibm-configuration'] && openApi['x-ibm-configuration']['enforced']) {
        accepts.push('application/json');
        accepts.push('application/xml');
      } else {
        accepts.push('application/json');
      }
    }
    return accepts;
  }
}
